import { Renderer } from '@src/renderer/Renderer';

import type {
  AssetPrewarmingService,
  CacheAssetsSummary,
} from './AssetPrewarmingService';
import type { TimelineAssetRef } from '@src/utility/timelineAssetUrls';

/**
 * A `Renderer` that knows how to pre-warm its own Creatomate-iframe asset
 * cache. Adds two thin methods (`warmAsset`, `warmAssets`) that delegate
 * to `AssetPrewarmingService`, and overrides `dispose()` to evict this
 * renderer's tracking state from the service automatically.
 *
 * Why a subclass, not a wrapper or hook:
 *
 *  - **Dispose discipline is unenforceable to break.** Any code path that
 *    calls `renderer.dispose()` triggers `forgetRenderer`. No matter who
 *    the caller is or whether they remembered the contract, the override
 *    runs. A `useManagedRenderer` hook would only catch the React-unmount
 *    path; a wrapper would still allow `wrapper.renderer.dispose()` to
 *    bypass cleanup.
 *  - **Callers don't see the service.** They have a `WarmedRenderer` and
 *    call `renderer.warmAsset(url)` — no `useAssetPrewarmingService()`
 *    hook, no `assetPrewarmingService.cacheAsset(renderer, url)` boilerplate.
 *  - **Layering stays clean.** This file lives in `src/services/`, not
 *    `src/renderer/`. The base `Renderer` keeps zero knowledge of caching;
 *    only the subclass (in services-land, where the cache lives) couples
 *    the two. No new layer-violation arrows.
 */
export class WarmedRenderer extends Renderer {
  private readonly _prewarming: AssetPrewarmingService;

  constructor(
    element: HTMLDivElement,
    mode: 'player' | 'interactive',
    publicToken: string,
    prewarming: AssetPrewarmingService,
  ) {
    super(element, mode, publicToken);
    this._prewarming = prewarming;
  }

  /**
   * Pre-warm a single asset URL into this renderer's CacheStorage.
   * Idempotent per (renderer, url) — repeat calls short-circuit.
   *
   * Throws on failure (callers handle / log / retry).
   */
  warmAsset(url: string): Promise<void> {
    return this._prewarming.cacheAsset(this, url);
  }

  /**
   * Bulk pre-warm — sort by playhead proximity, fan out with bounded
   * concurrency. Fail-soft per URL.
   */
  warmAssets(
    refs: TimelineAssetRef[],
    playheadSec: number,
  ): Promise<CacheAssetsSummary> {
    return this._prewarming.cacheAssets(this, refs, playheadSec);
  }

  /**
   * Tear down the iframe AND evict per-renderer cache tracking so the
   * service doesn't retain a dead reference. Always call this when done.
   */
  override dispose(): void {
    this._prewarming.forgetRenderer(this);
    super.dispose();
  }
}
